import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { useCallback, useMemo, useState } from 'react';
import type { Availability } from 'utils/booking';
import { dateFormatForAPI } from 'utils/date';
import moment from 'utils/dayjs';
import {
  formatBookingItems,
  formatRecurrencePayload,
} from '../../services/formatBasketPayload';

interface Slot {
  startTime: string;
  endTime: string;
}

interface Space {
  id: number;
  priceIncVat: number;
}

interface BookingItem {
  startTime: string;
  endTime: string;
  space: { id: number };
}

const getSpacePrice = (spaces: Space[], spaceId: number) => {
  const { priceIncVat = 0 } = spaces.find(({ id }) => id === spaceId) || {};
  return Number(priceIncVat);
};

// What POST /v1/spaces/availability returns under `slots`: one entry per
// requested time-of-day slot, each holding a per-date list of per-space states.
interface AvailabilityResponseSlot {
  startTime: string;
  endTime: string;
  entries?: Array<{
    date: string;
    availability: Array<{
      spaceId: number;
      state: string;
      durationInHours?: number;
    }>;
  }>;
}

// Flattens that into the `${startTime}-${endTime}`-keyed object the booking
// forms consume, pricing each row from the space's hourly rate.
const fixAvailability = (
  availability: AvailabilityResponseSlot[],
  spaces: Space[]
): Availability =>
  availability
    // `.map`, not `.flatMap`: the callback returns one object per slot, so there
    // is nothing to flatten — the reduce below merges them.
    .map(({ startTime, endTime, entries = [] }) => ({
      [`${startTime}-${endTime}`]: entries.flatMap(entry =>
        entry.availability.map(({ spaceId, state, durationInHours = 0 }) => ({
          spaceId,
          date: entry.date,
          priceWithVat: getSpacePrice(spaces, spaceId) * durationInHours,
          startTime,
          endTime,
          availabilityFlag: state,
        }))
      ),
    }))
    .reduce<Availability>((prev, curr) => ({ ...prev, ...curr }), {});

const formatTime = (time?: string) => moment(time).format('HH:mm');

const fixSlot = ({ startTime, endTime }: Slot) => ({ startTime, endTime });
const fixBookingItem = ({
  startTime,
  endTime,
  space: { id: spaceId } = {},
}: {
  startTime?: string;
  endTime?: string;
  space?: { id?: number };
}) => ({
  date: moment(startTime).format(dateFormatForAPI),
  startTime: formatTime(startTime),
  endTime: formatTime(endTime),
  spaceId,
});

const emptyObject = {};
const emptyList: number[] = [];

const useAvailability = (
  date: Date | string,
  clientServiceId: string,
  spaces: Space[],
  slots: Slot[],
  recurrence: object = emptyObject,
  bookingItems: object | BookingItem[] = emptyObject,
  bookingId: number | number[] = emptyList
) => {
  const [availability, setAvailability] = useState<Availability>({});

  const apiRequest = useApiRequest();

  const requestValid =
    date &&
    clientServiceId &&
    spaces.length > 0 &&
    slots.length > 0 &&
    slots[0].startTime &&
    slots[0]?.endTime;

  const usingRecurrence = bookingItems === emptyObject;

  const paramsJson = useMemo(
    () =>
      JSON.stringify({
        date: moment(date).format(dateFormatForAPI),
        clientServiceId,
        spaceIds: spaces.map(({ id }) => id),
        slots: slots.map(fixSlot),
        ...(!usingRecurrence || ('checked' in recurrence && !recurrence.checked)
          ? {}
          : {
              recurrence: formatRecurrencePayload(
                recurrence as Parameters<typeof formatRecurrencePayload>[0]
              ),
            }),
        ...(usingRecurrence
          ? {}
          : {
              bookingItems: Array.isArray(bookingItems)
                ? bookingItems.map(fixBookingItem)
                : formatBookingItems(
                    bookingItems as Parameters<typeof formatBookingItems>[0]
                  ),
            }),
        ...(Array.isArray(bookingId) && bookingId.length === 0
          ? {}
          : {
              excludedBookingIds: Array.isArray(bookingId)
                ? bookingId
                : [bookingId],
            }),
      }),
    [
      date,
      clientServiceId,
      usingRecurrence,
      recurrence,
      bookingItems,
      spaces,
      slots,
      bookingId,
    ]
  );

  const spacesJson = useMemo(() => JSON.stringify(spaces), [spaces]);

  const clearData = useCallback(async () => {
    setAvailability({});
  }, []);

  const fetchData = useCallback(async () => {
    const {
      data: { slots = [] },
    } = await apiRequest.post(
      `/v1/spaces/availability`,
      JSON.parse(paramsJson)
    );

    setAvailability(fixAvailability(slots, JSON.parse(spacesJson)));
  }, [apiRequest, paramsJson, spacesJson]);

  const { loading, error, refresh } = useAsync(
    requestValid ? fetchData : clearData,
    !!requestValid
  );

  return { data: availability, loading, error, refresh };
};

export default useAvailability;
